import React, { useCallback, useEffect, useState } from 'react';
import _ from "lodash";

import {
  AppAlert,
  AppButton,
  AppCheckbox,
  AppIcon,
  AppIconButton,
  AppMultiActionButton,
  AppToggleSwitch,
  MotifAppSelect
} from '../../../theme';
import { circleInfo, xMark } from '../../../theme/icons';
import { UserTypes } from '../../../utils/Constants';
import { IRoles } from '../../../models/IRoles';
import t from "../../../localization/en/translation.json";
import { Project } from '../../../components/createProject/models/project';
import { gridAppPlatform, gridAppPlatformKeys, USER_ACTIONS } from '../listUsers/listUserConstants';
import { AppPlatformClass } from '../../../components/appCard/models/appPlatformClass';
import { AppDetail } from '../../../components/createProject/models/appDetail';
import { lookupModel } from '../../createNewProject/lookupModel';
import { AppPlatformDetail } from '../../../components/createProject/models/appPlatformDetail';
import { isAppActive } from '../../../utils/helper-utility';
import { SSPRolesEnum } from "../../../utils/Constants";
import { IUpdateManageUsersResponse } from '../../../models/IUserAccess';
import AppSearchDropdown from '../../../components/appSearchDropdown/appSearchDropdown';

import './bulkRoleAssignment.scss';

export const ShowBulkRoleAssignment = ({
  record,
  closeRoleModal,
  platformRoles,
  users,
  updateManageUsersResponse,
  projectAppList,
  appPlatformList,
  projectStatuses,
  projectAppPlatformList,
  isProjectAdmin,
  isProjectAdminUser,
  isEyUser,
  updateManageUser,
  updateManageUserReset,
  showLoader
}:
  {
    record: Project,
    closeRoleModal: () => void,
    platformRoles: IRoles,
    users: any[],
    updateManageUsersResponse: IUpdateManageUsersResponse;
    projectAppList: AppDetail[],
    appPlatformList: AppPlatformClass[],
    projectStatuses: lookupModel[],
    projectAppPlatformList: AppPlatformDetail[],
    isProjectAdmin: boolean,
    isProjectAdminUser: boolean,
    isEyUser: boolean,
    updateManageUser: Function,
    updateManageUserReset: Function
    showLoader: Function
  }) => {

  const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
  const [isProjectRolesEnabled, setIsProjectRolesEnabled] = useState<boolean>(true);
  const [selectedProjectRole, setSelectedProjectRole] = useState<string>("");
  const [isPlatformRolesEnabled, setIsPlatformRolesEnabled] = useState<{ name: string, enabled: boolean }[]>([]);
  const [selectedPlatformRole, setSelectedPlatformRole] = useState<{ name: string, id: string, value: string[] | string }[]>([]);
  const [activePlatforms, setActivePlatforms] = useState<any[]>([]);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [alertMessageType, setAlertMessageType] = useState<"info" | "error" | "warning" | "success">("info");
  const [saveAction, setSaveAction] = useState<string>("");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);

  const handleUserSelection = (userId: string) => {
    setSelectedUsers((prevSelected) => prevSelected.map(user =>
      user.id === userId ? { ...user, isSelected: !user.isSelected } : user
    ));
  };

  const getAdditionalPlatforms = useCallback((
    projectAppList: AppDetail[],
    appPlatformList: AppPlatformClass[],
    projectStatuses: lookupModel[],
    appPlatformDetails: AppPlatformDetail[],
    isProjectAdmin: boolean,
    isProjectAdminUser: boolean,
    isEyUser: boolean,
  ) => {
    const additionalPlatforms: any[] = [];
    const groupedApps = _.groupBy(projectAppList, (item: AppDetail) => item.appPlatformId);

    gridAppPlatform.forEach((gridPlatform) => {
      const appPlatform = appPlatformList.find(
        (appPlatform: AppPlatformClass) => {
          return appPlatform.key === gridPlatform.key;
        }
      );

      if (appPlatform) {
        if (Object.keys(groupedApps).find((item) => item === appPlatform.id)) {
          const isAppPlatformActive = isAppActive(
            projectStatuses,
            appPlatformDetails,
            appPlatform.id
          );

          //either ProjectAdminUser(External or internal) OR ProjectAdmin(Always be internal)
          const canUpdatePlatformRoles = isProjectAdminUser || (isProjectAdmin && isEyUser);

          const filterPlatformRoleOptions = ({ key, multiSelect }) => {
            const selectedPlatformRoles = platformRoles.appPlatforms.find((item) => item?.appPlatformKey === key);
            let selectedRolesOptions = selectedPlatformRoles?.roles?.filter((item) => item.name) || [];
            let rolesOptions = [{ id: "", name: t.manage_users_role_placeholder, }, ...selectedRolesOptions];

            if (multiSelect)
              rolesOptions = [...selectedRolesOptions];

            return rolesOptions.map((item) => ({
              value: item.id,
              label: item.name,
            }));
          };

          additionalPlatforms.push({
            id: appPlatform.id,
            name: appPlatform.name,
            key: appPlatform.key,
            field: gridPlatform.key,
            canUpdatePlatformRoles: canUpdatePlatformRoles,
            isMultiOptions: appPlatform.isMultiRole,
            options: filterPlatformRoleOptions({ key: gridPlatform.key, multiSelect: appPlatform.isMultiRole }),
            isAppPlatformActive: isAppPlatformActive
          });

          setIsPlatformRolesEnabled(prev => [...prev, { name: appPlatform.name, enabled: true }]);
          setSelectedPlatformRole(prev => [...prev, {
            name: appPlatform.name,
            id: appPlatform.id,
            value: appPlatform.isMultiRole ? [] : ""
          }]);

        }
      }
    });

    return additionalPlatforms;
  }, [platformRoles]);

  const getAppPlatforms = useCallback(() => {
    const additionalPlatforms = getAdditionalPlatforms(
      projectAppList,
      appPlatformList,
      projectStatuses,
      projectAppPlatformList,
      isProjectAdmin,
      isProjectAdminUser,
      isEyUser
    ) || []

    setActivePlatforms(additionalPlatforms);

  }, [projectAppList, appPlatformList, projectStatuses, projectAppPlatformList, isProjectAdmin, isProjectAdminUser, isEyUser, getAdditionalPlatforms]);

  const saveAssignedRoles = (action: string) => {
    showLoader(true);
    setSaveAction(action);
    const payload = {
      projectId: record.id,
      body: {
        projectId: record.id,
        users: selectedUsers.filter(user => user.isSelected).map(user => ({
          action: USER_ACTIONS.UPDATE,
          user: user.id,
          userId: user.id,
          mail: user.userEmail,
          givenName: user.userFname,
          surname: user.userLname,
          language: user.userLanguage,
          country: user.userCountry,
          platformRoles: user.platformRoles || [],
        }))
      }
    };

    if (selectedProjectRole) {
      payload.body.users = payload.body.users.map(user => {
        return {
          ...user,
          ssp: {
            newRoles: [selectedProjectRole]
          }
        }
      });
    }
    if (selectedPlatformRole.length) {
      payload.body.users = payload.body.users.map(user => {
        if (selectedPlatformRole.some(role => role.value.length)) {
          return {
            ...user,
            appPlatforms: selectedPlatformRole.filter(role => role.value.length).map(role => {
              const combinedRoles = [...(role.value instanceof Array ? role.value || [] : [role.value]), ...(user.platformRoles?.find(item => item.id === role.id)?.value || [])];
              return ({
                appPlatformId: role.id,
                appPlatformName: role.name,
                newRoles: combinedRoles,
                oldRoles: user.platformRoles?.find(item => item.id === role.id)?.value || []
              });
            })
          }
        }
        else
          return user;
      });
    }
    updateManageUser(payload);
  };

  const clearAlert = () => {
    setAlertMessage("");
    setAlertMessageType("info");
  }

  const onToggleProject = () => {
    if (isProjectRolesEnabled) {
      setSelectedProjectRole("");
    }
    setIsProjectRolesEnabled((prev) => !prev);
  }

  const onTogglePlatform = (platform) => {
    if (isPlatformRolesEnabled.find(item => item.name === platform.name)?.enabled) {
      setSelectedPlatformRole(prev => prev.map(role => role.name === platform.name ? { ...role, value: [] } : role));
    }
    setIsPlatformRolesEnabled(prev => prev.map(item => item.name === platform.name ? { ...item, enabled: !item.enabled } : item));
  }

  const isDisabledSaveAction = useCallback(() => {
    if (!selectedUsers.filter(user => user.isSelected).length) return true;
    if (!selectedProjectRole && !selectedPlatformRole.some(role => role.value.length)) return true;

    const isProjectAdminRoleSelected = platformRoles.ssp.roles.find(role => role.key === SSPRolesEnum.PRJADMIN)?.id === selectedProjectRole || false;
    if (selectedUsers.filter(user => user.isSelected && user.userType === UserTypes.External).length && isProjectAdminRoleSelected)
      return true;

    return false;
  }, [selectedUsers, selectedProjectRole, selectedPlatformRole, platformRoles]);

  const getAppPlatformRoles = useCallback(() => {
    const filteredRoles = isProjectAdmin ? platformRoles?.ssp?.roles : platformRoles?.ssp?.roles.filter(role => role.key !== SSPRolesEnum.PRJADMIN);
    return filteredRoles.map(role => ({ label: role.name, value: role.id })) || [];
  }, [platformRoles, isProjectAdmin])

  const showPlatformRoleSelection = useCallback((platform) => {
    return (!(platform.key === gridAppPlatformKeys.SHARE_POINT && record.isSharePointSyncEnabled) || platform.key !== gridAppPlatformKeys.SHARE_POINT)
  }, [record]);

  const onCloseRoleModal = () => {
    if (selectedProjectRole || selectedPlatformRole.some(role => role.value.length)) {
      setHasUnsavedChanges(true);
      clearAlert();
    } else {
      closeRoleModal();
    }
  }

  const onSaveUnsavedChanges = () => {
    const isProjectAdminRoleSelected = platformRoles.ssp.roles.find(role => role.key === SSPRolesEnum.PRJADMIN)?.id === selectedProjectRole || false;
    if (selectedUsers.filter(user => user.isSelected && user.userType === UserTypes.External).length && isProjectAdminRoleSelected) {
      setAlertMessage(t.admin_role_warning_for_external_user);
      setAlertMessageType("warning");
      setHasUnsavedChanges(false);
    } else {
      saveAssignedRoles(t.save_and_close);
    }
  }

  useEffect(() => {
    if (updateManageUsersResponse?.appPlatforms?.length) {
      const successUpdates = updateManageUsersResponse.appPlatforms.filter((item) => !item.isAllProcessed);
      if (!successUpdates.length) {
        setTimeout(() => {
          updateManageUserReset();
          setSelectedProjectRole('');
          setSelectedPlatformRole(selectedPlatformRole.map(role => ({ ...role, value: role.value instanceof Array ? [] : "" })));
          if (saveAction === t.save_and_close) {
            closeRoleModal();
          }
        }, 500);
      }
    }
  }, [updateManageUsersResponse, updateManageUserReset, closeRoleModal, saveAction, selectedPlatformRole]);

  useEffect(() => {
    const isProjectAdminRoleSelected = platformRoles.ssp.roles.find(role => role.key === SSPRolesEnum.PRJADMIN)?.id === selectedProjectRole || false;
    if (selectedUsers.filter(user => user.isSelected && user.userType === UserTypes.External).length && isProjectAdminRoleSelected) {
      setAlertMessage(t.admin_role_warning_for_external_user);
      setAlertMessageType("warning");
      setHasUnsavedChanges(false);
    } else {
      clearAlert();
    }
  }, [selectedUsers, selectedProjectRole, platformRoles]);

  useEffect(() => {
    if (users.length) {
      const usersList = users.map(user => { return { ...user, isSelected: true }; }) || [];
      setSelectedUsers(usersList);
    }
  }, [users]);

  useEffect(() => {
    getAppPlatforms();
  }, [getAppPlatforms]);

  return (
    <>
      <div className="bulk-role-assignment-container">
        <div className="modal-close-btn">
          <AppIconButton
            aria-label="close"
            className="modal-close-icon"
            onClick={onCloseRoleModal}
          >
            <AppIcon icon={xMark} />
          </AppIconButton>
        </div>
        {hasUnsavedChanges && (
          <AppAlert severity={'warning'} className="role-assign-unsaved-changes-alerts" onClose={() => { }}>
            <>
              {t.bulk_assign_roles_unsaved_changes_warning}
              <div className="unsaved-changes-actions">
                <span className="yes-button" role="button" onClick={onSaveUnsavedChanges}>Yes</span>
                <span className="no-button" role="button" onClick={closeRoleModal}>No</span>
              </div>
            </>
          </AppAlert>
        )}
        {alertMessage && alertMessageType &&
          <>
            <AppAlert severity={alertMessageType} className="bulk-role-assign-alerts" onClose={() => clearAlert()}>
              {alertMessage}
            </AppAlert>
            <br />
          </>
        }
        <div className="app-row">
          <div className="app-col-lg-6 app-col-12">
            <div className="users-list-container">
              <div className="users-list-title">{t.admin_menu_header_users}</div>
              <div className="users-list-content">
                {selectedUsers.map(user => (
                  <div className="users-list-item" key={user.id}>
                    <div className="d-flex">
                      <AppCheckbox
                        name={user.id || ""}
                        checked={user.isSelected || false}
                        onChange={() => handleUserSelection(user.id || "")}
                        children={
                          <>
                            <div className="selected-user-name">{user.userFname + ' ' + user.userLname}</div>
                            <div className="selected-user-email">{user.userEmail}</div>
                            {user.userType && user.userType === UserTypes.External && <div className="external-user-type">{user.userType}</div>}
                          </>
                        } />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className='app-col-lg-6 app-col-12'>
            <div className="roles-container" id="roles-container">
              <div className="roles-label-container">
                <label className="roles-label">Project</label>
                <AppToggleSwitch
                  checked={isProjectRolesEnabled}
                  onChange={onToggleProject}
                  id={`projectRolesSwitch`}
                  newDesign
                />
              </div>
              <div className="roles-select-container">
                <MotifAppSelect
                  value={selectedProjectRole}
                  disabled={!isProjectRolesEnabled}
                  displayEmpty={true}
                  placeholder="Select"
                  onChange={({ target: { value } }) =>
                    setSelectedProjectRole(value as string)
                  }
                  options={getAppPlatformRoles()}
                  menuProps={{
                    PaperProps: {
                      className: 'role-select-popover'
                    },
                  }}
                />
                {!isProjectRolesEnabled && <div className="role-selection-info">{t.bulk_assign_roles_select_toggle_info.replace("{|platform|}", t.project)}</div>}
              </div>
              {activePlatforms.map((platform, key) => {
                return (
                  <div key={key} className="platform-container">
                    <div className="roles-label-container">
                      <label className="roles-label">{platform.name}</label>
                      {showPlatformRoleSelection(platform) &&
                        <AppToggleSwitch
                          checked={isPlatformRolesEnabled.find(item => item.name === platform.name)?.enabled || false}
                          onChange={() => onTogglePlatform(platform)}
                          id={`projectRolesSwitch`}
                          newDesign
                        />
                      }
                    </div>
                    <div className="roles-select-container">
                      {!showPlatformRoleSelection(platform) && (
                        <div className="sharepoint-sync-enabled-info">{t.sharepoint_users_sync_info}</div>
                      )}
                      {showPlatformRoleSelection(platform) && platform.isMultiOptions &&
                        <AppSearchDropdown
                          width={250}
                          menuWidth={document.getElementById(`roles-container`)?.offsetWidth || 300}
                          menuHeight={240}
                          idSuffix={platform.key}
                          options={platform.options}
                          multiSelect={true}
                          defaultValue={platform.options.filter(option => (selectedPlatformRole.find(item => item.name === platform.name)?.value as string[] || []).includes(option.value))}
                          placeholder="Select"
                          isDisabled={!isPlatformRolesEnabled.find(item => item.name === platform.name)?.enabled}
                          onValueChange={(selectedOptions) => {
                            setSelectedPlatformRole(prev => prev.map(item => item.name === platform.name ? { ...item, value: selectedOptions.map(option => option.value) as string[] } : item));
                          }}
                        />
                      }
                      {!platform.isMultiOptions &&
                        <MotifAppSelect
                          value={(selectedPlatformRole.find(item => item.name === platform.name)?.value as string || "")}
                          disabled={!isPlatformRolesEnabled.find(item => item.name === platform.name)?.enabled}
                          displayEmpty={true}
                          placeholder="Select"
                          onChange={({ target: { value } }) => {
                            setSelectedPlatformRole(prev => prev.map(item => item.name === platform.name ? { ...item, value: value as string } : item));
                          }}
                          options={platform.options || []}
                          menuProps={{
                            PaperProps: {
                              className: 'role-select-popover'
                            },
                          }}
                        />
                      }
                      {showPlatformRoleSelection(platform) &&
                        <div className="role-selection-info">{!isPlatformRolesEnabled.find(item => item.name === platform.name)?.enabled && <>{t.bulk_assign_roles_select_toggle_info.replace("{|platform|}", platform.name)}</>}</div>
                      }
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
        <div className="app-row">
          <div className="app-col-lg-12">
            <div className="notification-label-container"><AppIcon icon={circleInfo} style={{ marginRight: 10 }} />
              <span>{t.bulk_assign_roles_changes_info_msg}</span>
            </div>
          </div>
        </div>
        <div className="app-row">
          <div id="role-grid-footer" className="grid-footer">
            <div className="footer-btn-container">
              <AppButton color="secondary" className="cancel-btn" onClick={onCloseRoleModal}> Cancel </AppButton>
              <AppMultiActionButton disabled={isDisabledSaveAction()} actions={[
                { label: t.save_and_close, onClick: () => saveAssignedRoles(t.save_and_close) },
                { label: t.save, onClick: () => saveAssignedRoles(t.save) }
              ]} containerId="notification-label-container" />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
